export enum ChromeMessageType {
  CONTENT_SCRIPT,
  GET_TRACKING_DATA,
  EXTENSION_CONFIGURED,
  UPLOAD_LOGS_AND_CLEANUP,
  SHOULD_RESTORE_CONTENT,
  CANCEL_TAMPERING_CHECKS,
}

export type ChromeMessage =
  | { type: ChromeMessageType.CONTENT_SCRIPT }
  | { type: ChromeMessageType.GET_TRACKING_DATA }
  | { type: ChromeMessageType.EXTENSION_CONFIGURED }
  | { type: ChromeMessageType.UPLOAD_LOGS_AND_CLEANUP }
  | { type: ChromeMessageType.SHOULD_RESTORE_CONTENT }
  | { type: ChromeMessageType.CANCEL_TAMPERING_CHECKS };

export type EventsReportResult = {
  devicePolicyTimestamp: string | null;
  countableSeconds: number;
};

/** Normalized routine kinds persisted in `RulesStore.routines`. */
export const ROUTINE_RULE_TYPE = {
  SCREEN_FREE_TIME: 'screenFreeTime',
  CONTENT_FILTERING: 'contentFiltering',
} as const;

export type RoutineRuleType =
  (typeof ROUTINE_RULE_TYPE)[keyof typeof ROUTINE_RULE_TYPE];

export type RoutineRuleDefaultDomainCategoriesAction =
  | 'BLOCK_ALL'
  | 'ALLOW_ALL';

type RoutineRuleBase = {
  readonly id: string;
  readonly name: string;
  readonly activeUntil?: string;
  readonly countsTowardsDailyTimeQuota: boolean;
};

/** `fetchRules.ts` maps lock-navigation / lock-device QAPI policies to this shape. */
export type ScreenFreeTimeRoutineRule = RoutineRuleBase & {
  readonly type: typeof ROUTINE_RULE_TYPE.SCREEN_FREE_TIME;
};

/** `fetchRules.ts` maps filter-routine QAPI policies to this shape. */
export type ContentFilteringRoutineRule = RoutineRuleBase & {
  readonly type: typeof ROUTINE_RULE_TYPE.CONTENT_FILTERING;
  readonly defaultDomainCategoriesAction: RoutineRuleDefaultDomainCategoriesAction;
  readonly domainCategoriesExceptions: readonly number[];
};

export type RoutineRule =
  | ScreenFreeTimeRoutineRule
  | ContentFilteringRoutineRule;

export const ProcessUrlResponseType = {
  BLOCK: 'block',
  BLOCK_BY_QUOTA: 'blockByQuota',
  BLOCK_BY_SCHEDULE: 'blockBySchedule',
  BLOCK_BY_PAUSE: 'blockByPause',
  BLOCK_BY_SCREEN_FREE_TIME_ROUTINE: 'blockByScreenFreeTimeRoutine',
  BLOCK_BY_CONTENT_FILTERING_ROUTINE: 'blockByContentFilteringRoutine',
  ALLOW: 'allow',
  PENDING: 'pending',
  REDIRECT_TO_SAFE_SEARCH: 'redirectToSafeSearch',
  IGNORE: 'ignore',
} as const;

export type BlockProcessUrlResponse = {
  readonly type: typeof ProcessUrlResponseType.BLOCK;
};

export type BlockByQuotaProcessUrlResponse = {
  readonly type: typeof ProcessUrlResponseType.BLOCK_BY_QUOTA;
};

export type BlockByScheduleProcessUrlResponse = {
  readonly type: typeof ProcessUrlResponseType.BLOCK_BY_SCHEDULE;
  readonly nextAvailableHour?: number;
};

export type BlockByPauseProcessUrlResponse = {
  readonly type: typeof ProcessUrlResponseType.BLOCK_BY_PAUSE;
  readonly hours: number;
  readonly minutes: number;
};

export type BlockByScreenFreeTimeRoutineProcessUrlResponse = {
  readonly type: typeof ProcessUrlResponseType.BLOCK_BY_SCREEN_FREE_TIME_ROUTINE;
  readonly hours?: number;
  readonly minutes?: number;
};

export type BlockByContentFilteringRoutineProcessUrlResponse = {
  readonly type: typeof ProcessUrlResponseType.BLOCK_BY_CONTENT_FILTERING_ROUTINE;
  readonly categories?: readonly number[];
};

export type AllowProcessUrlResponse = {
  readonly type: typeof ProcessUrlResponseType.ALLOW;
};

export type PendingProcessUrlResponse = {
  readonly type: typeof ProcessUrlResponseType.PENDING;
};

export type RedirectToSafeSearchProcessUrlResponse = {
  readonly type: typeof ProcessUrlResponseType.REDIRECT_TO_SAFE_SEARCH;
};

export type IgnoreProcessUrlResponse = {
  readonly type: typeof ProcessUrlResponseType.IGNORE;
};

export type ProcessUrlResponse =
  | BlockProcessUrlResponse
  | BlockByQuotaProcessUrlResponse
  | BlockByScheduleProcessUrlResponse
  | BlockByPauseProcessUrlResponse
  | BlockByScreenFreeTimeRoutineProcessUrlResponse
  | BlockByContentFilteringRoutineProcessUrlResponse
  | AllowProcessUrlResponse
  | PendingProcessUrlResponse
  | RedirectToSafeSearchProcessUrlResponse
  | IgnoreProcessUrlResponse;

const BLOCK: BlockProcessUrlResponse = Object.freeze({
  type: ProcessUrlResponseType.BLOCK,
});
const BLOCK_BY_QUOTA: BlockByQuotaProcessUrlResponse = Object.freeze({
  type: ProcessUrlResponseType.BLOCK_BY_QUOTA,
});
const BLOCK_BY_SCREEN_FREE_TIME_ROUTINE: BlockByScreenFreeTimeRoutineProcessUrlResponse =
  Object.freeze({
    type: ProcessUrlResponseType.BLOCK_BY_SCREEN_FREE_TIME_ROUTINE,
  });
const BLOCK_BY_CONTENT_FILTERING_ROUTINE: BlockByContentFilteringRoutineProcessUrlResponse =
  Object.freeze({
    type: ProcessUrlResponseType.BLOCK_BY_CONTENT_FILTERING_ROUTINE,
  });
const ALLOW: AllowProcessUrlResponse = Object.freeze({
  type: ProcessUrlResponseType.ALLOW,
});
const PENDING: PendingProcessUrlResponse = Object.freeze({
  type: ProcessUrlResponseType.PENDING,
});
const REDIRECT_TO_SAFE_SEARCH: RedirectToSafeSearchProcessUrlResponse =
  Object.freeze({
    type: ProcessUrlResponseType.REDIRECT_TO_SAFE_SEARCH,
  });
const IGNORE: IgnoreProcessUrlResponse = Object.freeze({
  type: ProcessUrlResponseType.IGNORE,
});

/**
 * Frozen singleton verdicts prevent shared routing decisions from being
 * mutated while leaving room for future payload-bearing variants to allocate
 * fresh objects.
 */
export const processUrlResponse = {
  block: (): BlockProcessUrlResponse => BLOCK,
  blockByQuota: (): BlockByQuotaProcessUrlResponse => BLOCK_BY_QUOTA,
  blockBySchedule: (payload?: {
    nextAvailableHour?: number;
  }): BlockByScheduleProcessUrlResponse => ({
    type: ProcessUrlResponseType.BLOCK_BY_SCHEDULE,
    ...(payload !== undefined &&
      payload.nextAvailableHour !== undefined && {
        nextAvailableHour: payload.nextAvailableHour,
      }),
  }),
  blockByPause: (payload: {
    hours: number;
    minutes: number;
  }): BlockByPauseProcessUrlResponse => ({
    type: ProcessUrlResponseType.BLOCK_BY_PAUSE,
    hours: payload.hours,
    minutes: payload.minutes,
  }),
  blockByScreenFreeTimeRoutine: (payload?: {
    hours: number;
    minutes: number;
  }): BlockByScreenFreeTimeRoutineProcessUrlResponse =>
    payload === undefined
      ? BLOCK_BY_SCREEN_FREE_TIME_ROUTINE
      : {
          type: ProcessUrlResponseType.BLOCK_BY_SCREEN_FREE_TIME_ROUTINE,
          hours: payload.hours,
          minutes: payload.minutes,
        },
  blockByContentFilteringRoutine: (payload?: {
    categories: readonly number[];
  }): BlockByContentFilteringRoutineProcessUrlResponse =>
    payload === undefined
      ? BLOCK_BY_CONTENT_FILTERING_ROUTINE
      : {
          type: ProcessUrlResponseType.BLOCK_BY_CONTENT_FILTERING_ROUTINE,
          categories: payload.categories,
        },
  allow: (): AllowProcessUrlResponse => ALLOW,
  pending: (): PendingProcessUrlResponse => PENDING,
  redirectToSafeSearch: (): RedirectToSafeSearchProcessUrlResponse =>
    REDIRECT_TO_SAFE_SEARCH,
  ignore: (): IgnoreProcessUrlResponse => IGNORE,
} as const;

export type TrackingData = {
  deviceUid: string;
  accountUid: string;
  profileId: number;
  profileName: string;
};

export type SegmentProperties = {
  device_uid?: string;
  profile_id?: number;
  profile_name?: string;
};

export type ValueOf<T> = T[keyof T];

// eslint-disable-next-line @typescript-eslint/ban-types
export type DeepPartial<T> = T extends Function
  ? T
  : T extends Array<infer U>
  ? Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U>
  ? ReadonlyArray<DeepPartial<U>>
  : T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

/**
 * if the type is an array, return the type of the array's items
 */
export type UnArray<T> = T extends Array<infer U> ? U : T;

export class DeviceAndUserStore {}
